"use client"

import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import RefinementList from "../refinement-list"
import { SortOptions } from "../refinement-list/sort-products"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type VariantOption = {
  id: string
  title: string
  values: string[]
}

type Subcategory = {
  id: string
  name: string
  handle: string
}

type MobileFilterProps = {
  sortBy: SortOptions
  productOptions?: VariantOption[]
  subcategories?: Subcategory[]
}

export default function MobileFilter({ sortBy, productOptions, subcategories }: MobileFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()

  // Close filter when route changes
  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  // Prevent body scroll when filter is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  return (
    <>
      {/* Filter Button - Mobile Only */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-brown-dark text-cream px-6 py-3 rounded-full shadow-lg hover:bg-brown-light transition-colors flex items-center gap-2"
        aria-label="Toggle filters"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="font-medium">Filters</span>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-up Filter Panel */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-cream rounded-t-2xl shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "85vh" }}
      >
        <div className="flex flex-col" style={{ height: "85vh" }}>
          {/* Filter Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-beige">
            <h2 className="text-xl font-serif font-bold text-brown-dark">Filters & Sort</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-beige-light rounded-full transition-colors text-brown-dark"
              aria-label="Close filters"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Filter Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6" style={{ minHeight: 0 }}>
            <RefinementList 
              sortBy={sortBy} 
              productOptions={productOptions}
            />

            {/* Subcategories */}
            {subcategories && subcategories.length > 0 && (
              <div className="mt-8 pt-6 border-t border-beige">
                <h3 className="text-lg font-serif font-semibold text-brown-dark mb-4">
                  Subcategories
                </h3>
                <ul className="space-y-2">
                  {subcategories.map((subcat) => (
                    <li key={subcat.id}>
                      <LocalizedClientLink
                        href={`/categories/${subcat.handle}`}
                        className="block px-4 py-3 text-base font-medium text-brown-dark hover:bg-beige-light rounded-lg transition-colors"
                      >
                        {subcat.name}
                      </LocalizedClientLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Apply Button */}
          <div className="flex-shrink-0 border-t border-beige p-4 bg-cream">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full bg-brown-dark text-cream py-3 rounded-lg font-medium hover:bg-brown-light transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
